import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../pages/OrgAdmin.css';
import '../pages/SystemAdmin.css';
import './MasterAdminMaintenancePreview.css';

const MAINTENANCE_PREVIEW_SCROLL_OVERRIDE = `
html, body, #root {
  overflow-x: hidden !important;
  overflow-y: auto !important;
  height: auto !important;
  min-height: 100% !important;
  max-height: none !important;
}
.page-center {
  min-height: auto !important;
  height: auto !important;
  justify-content: flex-start !important;
  align-items: center !important;
  padding-top: 1rem !important;
  padding-bottom: 2rem !important;
}
.register-code-screen {
  overflow-y: visible !important;
  min-height: auto !important;
  height: auto !important;
  width: 100% !important;
}
@media (min-width: 768px) {
  html:has(.registration-split-screen),
  html:has(.registration-split-screen) body,
  html:has(.registration-split-screen) #root {
    overflow: hidden !important;
    height: 100% !important;
    min-height: 100% !important;
    max-height: 100vh !important;
  }
  .registration-split-screen {
    display: flex !important;
    flex-direction: row !important;
    align-items: flex-start !important;
    height: 100vh !important;
    min-height: 100vh !important;
    max-height: 100vh !important;
    overflow: hidden !important;
  }
  .registration-split-brand {
    width: 30% !important;
    flex: none !important;
    max-width: 30% !important;
    position: fixed !important;
    left: 0 !important;
    top: 0 !important;
    height: 100vh !important;
    max-height: 100vh !important;
    min-height: 0 !important;
    overflow: hidden !important;
    padding: 0 !important;
    background: var(--bg-canvas) !important;
    background-color: var(--bg-canvas) !important;
    z-index: 2 !important;
    transform: translateZ(0);
  }
  .registration-split-logo-anchor {
    height: 30vh !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100% !important;
    background: transparent !important;
    background-color: transparent !important;
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
  }
  .registration-split-logo,
  .registration-split-brand img {
    background: transparent !important;
    background-color: transparent !important;
    filter: none !important;
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;
  }
  .registration-split-workspace {
    width: 70% !important;
    flex: 1 1 70% !important;
    max-width: 70% !important;
    margin-left: 30% !important;
    height: 100vh !important;
    max-height: 100vh !important;
    min-height: 0 !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    overscroll-behavior: contain !important;
  }
}
@media (max-width: 767px) {
  .registration-split-screen {
    flex-direction: column !important;
    height: auto !important;
    min-height: auto !important;
    overflow: visible !important;
  }
}
.register-org-screen {
  min-height: auto !important;
  height: auto !important;
}
.register-code-panel {
  max-height: none !important;
}
.service-agreement-panel-compact,
.service-agreement-panel-org {
  max-height: none !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}
.service-agreement-panel {
  display: flex !important;
  flex-direction: column !important;
}
.service-agreement-scroll {
  flex: 0 0 250px !important;
  height: 250px !important;
  max-height: 250px !important;
  min-height: 250px !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
}
.service-agreement-declaration {
  flex-shrink: 0 !important;
}
.admin-nav-item.logout {
  display: none !important;
}
.living-room-screen {
  height: 100% !important;
  max-height: 100% !important;
  min-height: 0 !important;
  overflow: hidden !important;
}
.living-room-stream,
.living-room-welcome {
  overflow-y: auto !important;
}
`;

function injectMaintenancePreviewScrollOverrides(iframe: HTMLIFrameElement | null) {
  if (!iframe) return;

  try {
    const doc = iframe.contentDocument;
    if (!doc) return;

    let styleEl = doc.getElementById('maintenance-preview-scroll-override');
    if (!styleEl) {
      styleEl = doc.createElement('style');
      styleEl.id = 'maintenance-preview-scroll-override';
      doc.head.appendChild(styleEl);
    }

    styleEl.textContent = MAINTENANCE_PREVIEW_SCROLL_OVERRIDE;

    doc.documentElement.style.overflowY = 'auto';
    doc.body.style.overflowY = 'auto';
    doc.documentElement.style.height = 'auto';
    doc.body.style.height = 'auto';
  } catch {
    /* Preview injection is best-effort for same-origin iframes only. */
  }
}

export interface MaintenancePreviewScreen {
  screenId: string;
  label: string;
  route: string;
  previewMode?: 'modal';
}

/** Maintenance Panel selection list — labels and preview route mappings only */
export const MAINTENANCE_PREVIEW_SIDEBAR_ITEMS: MaintenancePreviewScreen[] = [
  {
    screenId: 'Screen-ELT01',
    label: 'Screen-ELT01: Landing Page',
    route: '/',
  },
  {
    screenId: 'Screen-ELT02',
    label: 'Screen-ELT02: "Registration" Pathway Selector [Options A, B, C]',
    route: '/register-select',
  },
  {
    screenId: 'Screen-ELT08',
    label: 'Screen-ELT08: Human Mentor Console & Crisis Triage Workspace',
    route: '/mentor-console',
  },
  {
    screenId: 'Screen-PRO01',
    label: 'Screen-PRO01: Registration Key Entrance (Portal Login)',
    route: '/portal-login',
  },
  {
    screenId: 'Screen-PRO03',
    label: 'Screen-PRO03: Individual Mentee Stripe Checkout',
    route: '/register-private',
  },
  {
    screenId: 'Screen-ELT07',
    label: 'Screen-ELT07: Organisation Setup (Pathway A Card / Pathway B Procurement)',
    route: '/register-org',
  },
  {
    screenId: 'Screen-ELT04',
    label: 'Screen-ELT04: Organisation Demo Mode Setup',
    route: '/register-code?screen=elt04',
  },
  {
    screenId: 'Screen-PRO02',
    label: 'Screen-PRO02: Mentee Demo Mode Setup',
    route: '/register-mentee-code',
  },
  {
    screenId: 'Screen-ELT03',
    label: 'Screen-ELT03: Demo Key Authentication Gate',
    route: '/register-code',
  },
  {
    screenId: 'Screen-ELT05',
    label: 'Screen-ELT05: Executive Introduction Portal [Dr. Andre Intro]',
    route: '/introduction',
  },
  {
    screenId: 'Screen-ELT13',
    label: 'Screen-ELT13: Issued Access Activation Gate (Service Agreement)',
    route: '/issued-access',
  },
  {
    screenId: 'Screen-ELT10',
    label: 'Screen-ELT10: Organisation Mentee Setup (Mentor / Cloud)',
    route: '/elt-setup',
  },
  {
    screenId: 'Screen-PRO04',
    label: 'Screen-PRO04: Individual Setup Page',
    route: '/pro-setup',
  },
  {
    screenId: 'Screen-ELT06',
    label: 'Screen-ELT06: Mentoring Admin Console [Client Org Hub]',
    route: '/org-admin',
  },
  {
    screenId: 'Screen-PRO08',
    label: 'Screen-PRO08: Private Mentee Briefing Room',
    route: '/mentee-lounge',
  },
  {
    screenId: 'Screen-PRO08X',
    label: 'Screen-PRO08X: 10-Min Strategy Stress-Test (Guest Sandbox)',
    route: '/stress-test',
  },
  {
    screenId: 'Screen-ELT09',
    label: 'Screen-ELT09: Org Mentee Briefing Room',
    route: '/corporate-lounge',
  },
];

export default function MasterAdminMaintenancePreview() {
  const previewIframeRef = useRef<HTMLIFrameElement | null>(null);
  const [activeScreenId, setActiveScreenId] = useState(
    MAINTENANCE_PREVIEW_SIDEBAR_ITEMS[0]?.screenId ?? '',
  );

  const activeScreen = useMemo(
    () => MAINTENANCE_PREVIEW_SIDEBAR_ITEMS.find((item) => item.screenId === activeScreenId)
      ?? MAINTENANCE_PREVIEW_SIDEBAR_ITEMS[0],
    [activeScreenId],
  );

  const previewRouteLabel = activeScreen.previewMode === 'modal'
    ? `${activeScreen.route} modal`
    : activeScreen.route;

  const handlePreviewIframeLoad = useCallback(() => {
    injectMaintenancePreviewScrollOverrides(previewIframeRef.current);
  }, []);

  useEffect(() => {
    injectMaintenancePreviewScrollOverrides(previewIframeRef.current);
  }, [activeScreen.route, activeScreenId]);

  return (
    <div className="maintenance-preview-layout">
      <aside className="maintenance-preview-sidebar" aria-label="Maintenance Panel">
        <p className="master-admin-sidebar-heading maintenance-preview-sidebar-heading">Maintenance Panel</p>
        <div className="maintenance-preview-sidebar-nav max-h-screen overflow-y-scroll pb-12">
          {MAINTENANCE_PREVIEW_SIDEBAR_ITEMS.map((item) => (
            <button
              key={item.screenId}
              type="button"
              className={`maintenance-preview-nav-item ${activeScreenId === item.screenId ? 'active' : ''}`}
              onClick={() => setActiveScreenId(item.screenId)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </aside>
      <main className="maintenance-preview-panel">
        <div className="org-panel-card" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', height: '100%' }}>
          <p className="knowledge-hub-intro">Route: {previewRouteLabel}</p>
          <div className="maintenance-preview-viewport">
            <iframe
              ref={previewIframeRef}
              title={activeScreen.label}
              src={activeScreen.route}
              className="maintenance-preview-iframe"
              scrolling="yes"
              allow="autoplay; microphone"
              onLoad={handlePreviewIframeLoad}
            />
          </div>
        </div>
      </main>
    </div>
  );
}
